/** @format */

import { DeepPartial } from "@reduxjs/toolkit";
import { Locale } from "../../../features/i18n/init";
import { FullResource } from "../../../features/resources/ResourceFunctions";
import {
  UserResource,
  UserResourceReference,
} from "../../../features/resources/types";
import { store } from "../../store";
import { api } from "../api";

export interface SaveUserResourceBody {
  diff: DeepPartial<UserResource>;
  primaryKey: { PK: string; SK: string };
  userEmailHash: string;
  copy: number;
  locale: Locale;
}

interface CreateResourceBody {
  code: string;
  locale: Locale;
  copy?: number;
}

export const userResourceApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUserResource: build.query({
      query: ({ code, locale, copy, email }) => {
        if (store.getState().auth.userToken?.token) {
          return `user-resource/auth/get/${code}/${locale}/${copy}/${email}`;
        }
        return `user-resource/get/${code}/${locale}/${copy}/${email}`;
      },
      transformResponse: (response: { userResource: UserResource }) =>
        response.userResource,
      providesTags: ["Resource"],
    }),

    getUserResourceRef: build.query({
      query: ({ code, locale, copy, email }) =>
        `user-resource/get-reference/${code}/${locale}/${copy}/${email}`,
      providesTags: ["Resource"],
    }),

    getUserResourcesForUser: build.query({
      query: () => `user-resource/get-for-user`,
      transformResponse: (response: {
        userResourceRefs: UserResourceReference[];
      }) => response.userResourceRefs,
      providesTags: ["Resource"],
    }),

    createResource: build.mutation<
      { message: string; User: string; Copy: number },
      CreateResourceBody
    >({
      query(body) {
        return {
          url: "user-resource/create",
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Resource"],
    }),

    saveUserResource: build.mutation<{ message: string }, SaveUserResourceBody>(
      {
        query(body) {
          return {
            url: "user-resource/save-resource",
            method: "POST",
            body,
          };
        },
        invalidatesTags: ["Resource", "SubLesson", "Card", "ResourceStatus"],
      },
    ),

    printUserResource: build.query({
      query: ({ code, locale, copy, email }) => {
        if (store.getState().auth.userToken?.token) {
          return `user-resource/auth/print/${code}/${locale}/${copy}/${email}`;
        }
        return `user-resource/print/${code}/${locale}/${copy}/${email}`;
      },
      transformResponse: (response: { printableUserResource: FullResource }) =>
        response.printableUserResource,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUserResourceQuery,
  useSaveUserResourceMutation,
  useCreateResourceMutation,
  useGetUserResourcesForUserQuery,
  useGetUserResourceRefQuery,
  usePrintUserResourceQuery,
} = userResourceApi;
